// Dot-vote tally per shape per user. Layout:
//   votes: Y.Map<shapeId → Y.Map<userId → count>>
//
// We use nested Y.Maps (rather than a flat key like `${shapeId}:${userId}`)
// so a CRDT delete on a shape removes its vote subtree atomically via Y.Map
// observers downstream. Counts default to 0 / are deleted when they hit 0
// so the subtree stays small and `totalVotesUsed` is a sum.

import * as Y from 'yjs';
import { LOCAL_ORIGIN_RETRO } from './metaMap';

type VotesRoot = Y.Map<Y.Map<number>>;

export function getVotesMap(doc: Y.Doc): VotesRoot {
  return doc.getMap<Y.Map<number>>('votes');
}

function getOrCreateShapeVotes(root: VotesRoot, shapeId: string): Y.Map<number> {
  let m = root.get(shapeId);
  if (!m) {
    m = new Y.Map<number>();
    root.set(shapeId, m);
  }
  return m;
}

// Add one vote from `userId` on `shapeId`. Idempotent up to the per-user
// cap — if `cap` is provided and the user already has `cap` votes total
// across the board, this is a no-op.
export function castVote(doc: Y.Doc, shapeId: string, userId: string, cap?: number): void {
  doc.transact(() => {
    if (cap !== undefined) {
      const used = totalVotesUsed(doc, userId);
      if (used >= cap) return;
    }
    const shapeVotes = getOrCreateShapeVotes(getVotesMap(doc), shapeId);
    const cur = shapeVotes.get(userId) ?? 0;
    shapeVotes.set(userId, cur + 1);
  }, LOCAL_ORIGIN_RETRO);
}

// Remove one vote. If the user's count drops to 0 we delete the entry so
// the map stays compact and `votersFor` doesn't have to filter zeros out.
export function removeVote(doc: Y.Doc, shapeId: string, userId: string): void {
  doc.transact(() => {
    const root = getVotesMap(doc);
    const shapeVotes = root.get(shapeId);
    if (!shapeVotes) return;
    const cur = shapeVotes.get(userId) ?? 0;
    if (cur <= 1) {
      shapeVotes.delete(userId);
      // If nobody has voted on this shape anymore, drop the shape entry
      // entirely — keeps the root map size proportional to "shapes with votes"
      // rather than "shapes that have ever had a vote."
      if (shapeVotes.size === 0) root.delete(shapeId);
    } else {
      shapeVotes.set(userId, cur - 1);
    }
  }, LOCAL_ORIGIN_RETRO);
}

// Clear all votes on a shape (used when a shape is deleted; clears the row).
export function clearShapeVotes(doc: Y.Doc, shapeId: string): void {
  doc.transact(() => {
    getVotesMap(doc).delete(shapeId);
  }, LOCAL_ORIGIN_RETRO);
}

// Snapshot of votes-per-user for a single shape. Empty map when nothing exists.
export function votesForShape(doc: Y.Doc, shapeId: string): Map<string, number> {
  const out = new Map<string, number>();
  const shapeVotes = getVotesMap(doc).get(shapeId);
  if (!shapeVotes) return out;
  shapeVotes.forEach((count, userId) => {
    if (count > 0) out.set(userId, count);
  });
  return out;
}

// Sum across all shapes — the "used vs budget" denominator for a user.
export function totalVotesUsed(doc: Y.Doc, userId: string): number {
  let total = 0;
  getVotesMap(doc).forEach((shapeVotes) => {
    total += shapeVotes.get(userId) ?? 0;
  });
  return total;
}

// Total vote count on a shape (sum across all users). Used for the indicator
// dot count on the sticky.
export function totalVotesFor(doc: Y.Doc, shapeId: string): number {
  let total = 0;
  const shapeVotes = getVotesMap(doc).get(shapeId);
  if (!shapeVotes) return 0;
  shapeVotes.forEach((count) => {
    total += count;
  });
  return total;
}
